import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaCheck,
  FaUserPlus,
  FaHourglassHalf,
  FaIdCard,
  FaCalendarDay,
  FaInbox,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaRedo,
  FaSearchPlus,
  FaSearchMinus,
  FaFileAlt,
  FaExclamationTriangle,
  FaUserShield,
  FaChevronLeft,
  FaChevronRight,
  FaBan,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes arFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes arFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes arZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes arSlideUp { from { transform: translateY(100%); } to { transform: none; } }
@keyframes arPulse { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,.5); } 70% { box-shadow: 0 0 0 7px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
.ar-in { animation: arFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.ar-fade { animation: arFade .2s ease both; }
.ar-zoom { animation: arZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.ar-up { animation: arSlideUp .3s cubic-bezier(.2,.8,.2,1) both; }
.ar-dot { animation: arPulse 1.8s ease-out infinite; }
.ar-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease, box-shadow .2s ease; }
.ar-press:hover { transform: translateY(-1px); }
.ar-press:active { transform: scale(.96); }
.ar-item { transition: background-color .15s ease, border-color .2s ease, transform .15s ease; }
.ar-item:active { transform: scale(.99); }
.ar-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
.ar-img { transition: transform .25s ease; }
@media (prefers-reduced-motion: reduce) {
  .ar-in, .ar-fade, .ar-zoom, .ar-up, .ar-dot { animation: none; }
  .ar-press:hover, .ar-press:active, .ar-item:active { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

// Same folder the server uses for KYC files (util.php -> KYC_DOCUMENTS)
const KYC_DOCUMENTS = "https://ycnp.in/application/document/";

/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const fullName = (u) =>
  [u?.first_name, u?.middle_name, u?.last_name].filter((x) => x && String(x).trim()).join(" ") || "No name";
const place = (u) => [u?.village, u?.district, u?.state].filter((x) => x && String(x).trim()).join(", ");

const hasFile = (v) => v && v !== "0" && v !== "null";
const docUrl = (f) => (!hasFile(f) ? null : /^https?:\/\//.test(f) ? f : KYC_DOCUMENTS + f);

const waitingText = (d) => {
  if (!d) return "";
  const days = Math.floor((Date.now() - d.getTime()) / DAY);
  if (days <= 0) return "Today";
  if (days === 1) return "1 day waiting";
  return `${days} days waiting`;
};

const maskNum = (v, keep = 4) => {
  const s = String(v || "");
  if (s.length <= keep) return s;
  return "•".repeat(Math.max(0, s.length - keep)).replace(/(.{4})/g, "$1 ").trim() + " " + s.slice(-keep);
};

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

const docsOf = (u) => (Array.isArray(u?.doc) ? u.doc : []);

const checklist = (u) => {
  const docs = docsOf(u);
  return [
    { label: "Profile photo", ok: !!u?.profile_img && !/default|avatar|user\.png/i.test(u.profile_img) },
    { label: "Phone number", ok: !!u?.phone_num },
    { label: "Email", ok: !!u?.email_id },
    { label: "PAN card", ok: docs.some((d) => d.pan_no && hasFile(d.pan_img)) },
    { label: "Aadhaar card", ok: docs.some((d) => d.aadhar_no && hasFile(d.aadhar_img)) },
    { label: "Other document", ok: docs.some((d) => hasFile(d.doc_img)) },
  ];
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="ar-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 40 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return (
      <img src={src} alt="" onError={() => setErr(true)} className="rounded-full object-cover shrink-0 bg-slate-100" style={{ width: size, height: size }} />
    );
  return (
    <span className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </span>
  );
};

/* ================= page ================= */

const AdminReq = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("oldest");
  const [docFilter, setDocFilter] = useState("all"); // all | complete | missing
  const [selectedId, setSelectedId] = useState(null);
  const [checked, setChecked] = useState([]);
  const [showNums, setShowNums] = useState(false);
  const [viewer, setViewer] = useState(null); // {list:[{url,label}], index}
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [confirm, setConfirm] = useState(null); // {type:'approve'|'reject', users}
  const [working, setWorking] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/users.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await post("admin-request");
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch admin requests" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, users } = confirm;
    setWorking(true);
    let ok = 0;
    let needsServer = false;
    for (const u of users) {
      try {
        const data = await post(type === "approve" ? "add-admin" : "reject-admin", { user_id: u.user_id });
        if (data && data.status === false) {
          if (String(data.error || "").toLowerCase().includes("invalid action")) needsServer = true;
          throw new Error(data.error || "Failed");
        }
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);

    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: type === "approve" ? "Approved" : "Rejected",
        detail:
          type === "approve"
            ? ok === 1
              ? `${fullName(users[0])} is now an Admin`
              : `${ok} users are now Admins`
            : `${ok} request${ok > 1 ? "s" : ""} rejected`,
      });
    }
    if (needsServer) {
      toast.current?.show({
        severity: "warn",
        summary: "Server update needed",
        detail: "Reject is not available on the server yet. Add the 'reject-admin' action to users.php.",
        life: 7000,
      });
    } else if (ok < users.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${users.length - ok} request(s) failed` });
    }
    fetchRequests();
  };

  /* ----- derived ----- */

  const q = query.trim();

  const list = useMemo(() => {
    const ql = q.toLowerCase();
    let l = requests.filter((u) => {
      const cl = checklist(u);
      const complete = cl.filter((c) => c.label !== "Other document").every((c) => c.ok);
      if (docFilter === "complete" && !complete) return false;
      if (docFilter === "missing" && complete) return false;
      if (!ql) return true;
      const docs = docsOf(u);
      return [
        fullName(u),
        u.user_id,
        u.phone_num,
        u.email_id,
        place(u),
        u.address,
        u.profession,
        ...docs.map((d) => d.pan_no),
        ...docs.map((d) => d.aadhar_no),
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "name") return fullName(a).localeCompare(fullName(b));
      const da = parseAny(a.request_date_time)?.getTime() || 0;
      const db = parseAny(b.request_date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [requests, q, sortBy, docFilter]);

  useEffect(() => {
    if (!list.length) {
      setSelectedId(null);
      return;
    }
    if (!list.some((u) => u.user_id === selectedId)) setSelectedId(list[0].user_id);
  }, [list, selectedId]);

  useEffect(() => {
    const valid = new Set(requests.map((r) => r.user_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [requests]);

  const selected = list.find((u) => u.user_id === selectedId) || null;
  const selIndex = list.findIndex((u) => u.user_id === selectedId);

  const stats = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return {
      pending: requests.length,
      old: requests.filter((u) => {
        const d = parseAny(u.request_date_time);
        return d && Date.now() - d.getTime() > 3 * DAY;
      }).length,
      withDocs: requests.filter((u) => docsOf(u).length > 0).length,
      today: requests.filter((u) => {
        const d = parseAny(u.request_date_time);
        return d && d >= startOfToday;
      }).length,
    };
  }, [requests]);

  const checkedUsers = requests.filter((u) => checked.includes(u.user_id));
  const allChecked = list.length > 0 && list.every((u) => checked.includes(u.user_id));

  /* ----- keyboard ----- */

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (viewer) {
        if (e.key === "Escape") setViewer(null);
        if (e.key === "ArrowRight") setViewer((v) => ({ ...v, index: (v.index + 1) % v.list.length }));
        if (e.key === "ArrowLeft") setViewer((v) => ({ ...v, index: (v.index - 1 + v.list.length) % v.list.length }));
        return;
      }
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (typing) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if ((e.key === "ArrowDown" || e.key === "j") && selIndex < list.length - 1) setSelectedId(list[selIndex + 1].user_id);
      if ((e.key === "ArrowUp" || e.key === "k") && selIndex > 0) setSelectedId(list[selIndex - 1].user_id);
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer, confirm, working, selIndex, list]);

  useEffect(() => {
    setZoom(1);
    setRotate(0);
  }, [viewer?.index, viewer?.list]);

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const openViewer = (u, url) => {
    const files = [];
    docsOf(u).forEach((d) => {
      if (docUrl(d.pan_img)) files.push({ url: docUrl(d.pan_img), label: "PAN card" });
      if (docUrl(d.aadhar_img)) files.push({ url: docUrl(d.aadhar_img), label: "Aadhaar card" });
      if (docUrl(d.doc_img)) files.push({ url: docUrl(d.doc_img), label: d.doc_type || "Document" });
    });
    const index = Math.max(0, files.findIndex((f) => f.url === url));
    setViewer({ list: files, index });
  };

  /* ----- detail panel ----- */

  const Detail = ({ u }) => {
    if (!u) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center p-10">
          <FaUserShield className="text-slate-300" size={40} />
          <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">Select a request to review</p>
          <p className="text-sm text-slate-500">Details and documents will show here.</p>
        </div>
      );
    }
    const req = parseAny(u.request_date_time);
    const reg = parseAny(`${u.reg_date} ${u.reg_time || ""}`);
    const cl = checklist(u);
    const score = cl.filter((c) => c.ok).length;
    const docs = docsOf(u);

    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-4">
            <Avatar src={u.profile_img} name={fullName(u)} size={72} />
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white truncate">{fullName(u)}</h2>
              <button type="button" onClick={() => copyText(u.user_id, "User ID copied")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 tabular-nums">
                ID {u.user_id} <FaCopy size={10} />
              </button>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  <FaHourglassHalf size={10} /> {waitingText(req) || "Pending"}
                </span>
                {u.gender && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">{u.gender}</span>}
                {u.account_status && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${String(u.account_status).toLowerCase() === "block" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                    Account: {String(u.account_status).toLowerCase() === "block" ? "Blocked" : String(u.account_status).toLowerCase() === "live" ? "Active" : u.account_status}
                  </span>
                )}
              </div>
            </div>
            <button type="button" onClick={() => setMobileOpen(false)} className="lg:hidden ar-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100" aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Checklist */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Verification checklist</h3>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${score >= 5 ? "bg-emerald-100 text-emerald-700" : score >= 3 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                {score} of {cl.length}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden mb-3">
              <div className={`h-full rounded-full transition-all duration-500 ${score >= 5 ? "bg-emerald-500" : score >= 3 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${(score / cl.length) * 100}%` }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {cl.map((c) => (
                <div key={c.label} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${c.ok ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200" : "bg-slate-50 text-slate-500 dark:bg-slate-800/60"}`}>
                  {c.ok ? <FaCheckCircle className="text-emerald-500 shrink-0" /> : <FaTimesCircle className="text-slate-300 shrink-0" />}
                  {c.label}
                </div>
              ))}
            </div>
          </section>

          {/* Documents */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Uploaded documents</h3>
              {docs.some((d) => d.pan_no || d.aadhar_no) && (
                <button type="button" onClick={() => setShowNums((s) => !s)} className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
                  {showNums ? <FaEyeSlash size={11} /> : <FaEye size={11} />} {showNums ? "Hide numbers" : "Show numbers"}
                </button>
              )}
            </div>
            {docs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-6 text-center text-sm text-slate-500">
                <FaFileAlt className="mx-auto text-slate-300 mb-2" size={22} />
                No documents uploaded
              </div>
            ) : (
              <div className="space-y-3">
                {docs.map((d, i) => {
                  const files = [
                    { url: docUrl(d.pan_img), label: "PAN card", num: d.pan_no },
                    { url: docUrl(d.aadhar_img), label: "Aadhaar card", num: d.aadhar_no },
                    { url: docUrl(d.doc_img), label: d.doc_type || "Document" },
                  ];
                  return (
                    <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                      {d.doc_type && <p className="text-xs font-semibold text-slate-500 mb-2">{d.doc_type}</p>}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {files.map((f) => (
                          <div key={f.label} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                            <p className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                              <FaIdCard size={11} /> {f.label}
                            </p>
                            {f.url ? (
                              <button type="button" onClick={() => openViewer(u, f.url)} className="ar-press relative block w-full rounded-md overflow-hidden bg-slate-200 dark:bg-slate-700 group" style={{ aspectRatio: "4/3" }}>
                                <img src={f.url} alt={f.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover:bg-slate-900/40 text-white opacity-0 group-hover:opacity-100 transition">
                                  <FaSearchPlus />
                                </span>
                              </button>
                            ) : (
                              <div className="rounded-md bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs text-slate-400" style={{ aspectRatio: "4/3" }}>
                                Not uploaded
                              </div>
                            )}
                            {f.num && (
                              <button type="button" onClick={() => copyText(f.num, `${f.label} number copied`)} className="mt-1.5 w-full inline-flex items-center justify-between gap-1 text-xs font-mono text-slate-700 dark:text-slate-200 hover:text-cyan-700">
                                <span className="truncate">{showNums ? f.num : maskNum(f.num)}</span>
                                <FaCopy size={10} className="shrink-0" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Contact */}
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
            {[
              { icon: FaEnvelope, label: "Email", value: u.email_id, href: u.email_id ? `mailto:${u.email_id}` : null },
              { icon: FaPhoneAlt, label: "Contact Number", value: u.phone_num, href: u.phone_num ? `tel:${u.phone_num}` : null },
              { icon: FaMapMarkerAlt, label: "Address", value: [u.address, place(u), u.country].filter((x) => x && String(x).trim()).join(", ") },
            ].map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                  <Icon className="text-slate-400 shrink-0" size={13} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">{r.label}</p>
                    {r.value ? (
                      r.href ? (
                        <a href={r.href} className="text-sm text-slate-900 dark:text-white hover:underline break-all">{r.value}</a>
                      ) : (
                        <p className="text-sm text-slate-900 dark:text-white">{r.value}</p>
                      )
                    ) : (
                      <p className="text-sm text-slate-400">-</p>
                    )}
                  </div>
                  {r.value && (
                    <button type="button" onClick={() => copyText(r.value)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label={`Copy ${r.label}`}>
                      <FaCopy size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </section>

          {/* Info grid */}
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              ["DOB", u.dob],
              ["Country", u.country],
              ["Profession", u.profession],
              ["Study", u.study],
              ["Language", u.language],
              ["Referral code", u.reffral_code],
              ["Reg. Date", reg ? `${fmtDate(reg)}, ${fmtTime(reg)}` : `${u.reg_date || "-"} ${u.reg_time || ""}`],
              ["Request Date", req ? `${fmtDate(req)}, ${fmtTime(req)}` : u.request_date_time],
              ["Referred by", u.reffer_by],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                <dt className="text-xs text-slate-500">{k}</dt>
                <dd className="text-sm font-medium text-slate-900 dark:text-white break-words mt-0.5">{v || "-"}</dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-wrap gap-2">
            {waLink(u.phone_num) && (
              <a href={waLink(u.phone_num)} target="_blank" rel="noreferrer" className="ar-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm font-semibold">
                <FaWhatsapp /> WhatsApp
              </a>
            )}
            <button type="button" onClick={() => navigate(`/user-profile/${u.user_id}`)} className="ar-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <FaExternalLinkAlt size={11} /> Full profile
            </button>
          </div>
        </div>

        {/* Decision bar */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="hidden sm:flex gap-1 mr-auto">
            <button type="button" disabled={selIndex <= 0} onClick={() => setSelectedId(list[selIndex - 1].user_id)} className="ar-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Previous request">
              <FaChevronLeft size={11} />
            </button>
            <button type="button" disabled={selIndex >= list.length - 1} onClick={() => setSelectedId(list[selIndex + 1].user_id)} className="ar-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Next request">
              <FaChevronRight size={11} />
            </button>
          </div>
          <button type="button" onClick={() => setConfirm({ type: "reject", users: [u] })} className="ar-press flex-1 sm:flex-none h-11 px-5 rounded-lg border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-600 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-rose-50">
            <FaBan size={12} /> Reject
          </button>
          <button type="button" onClick={() => setConfirm({ type: "approve", users: [u] })} className="ar-press flex-1 sm:flex-none h-11 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-sm">
            <FaCheck size={12} /> Accept Request
          </button>
        </div>
      </div>
    );
  };

  /* ================= render ================= */

  const statCards = [
    { label: "Pending requests", value: stats.pending, icon: FaUserPlus, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300", onClick: () => setDocFilter("all") },
    { label: "Waiting 3+ days", value: stats.old, icon: FaHourglassHalf, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300", onClick: () => setSortBy("oldest") },
    { label: "With documents", value: stats.withDocs, icon: FaIdCard, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setDocFilter("complete") },
    { label: "Came today", value: stats.today, icon: FaCalendarDay, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300", onClick: () => setSortBy("newest") },
  ];

  const cur = viewer ? viewer.list[viewer.index] : null;

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="ar-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Users</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Admin Request</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Review who wants to become an admin manager, check their documents and decide.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/users/admin")} className="ar-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50">
            <FaUserShield size={12} /> Current admins
          </button>
          <button type="button" onClick={fetchRequests} disabled={loading} className="ar-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <button key={c.label} type="button" onClick={c.onClick} className={`${CARD} ar-in ar-press p-4 flex items-center gap-4 text-left`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className={`${CARD} ar-in mt-4 p-4`} style={{ animationDelay: "240ms" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, user ID, phone, email, location, PAN or Aadhaar number"
              className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {query ? (
                <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={13} />
                </button>
              ) : (
                <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
              )}
            </div>
          </div>
          <select value={docFilter} onChange={(e) => setDocFilter(e.target.value)} className={`${INPUT} h-12 px-3 md:w-48`}>
            <option value="all">All requests</option>
            <option value="complete">Documents complete</option>
            <option value="missing">Something missing</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-12 px-3 md:w-44`}>
            <option value="oldest">Waiting longest</option>
            <option value="newest">Newest first</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      </div>

      {/* Bulk bar */}
      {checked.length > 0 && (
        <div className="ar-fade mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 px-4 py-2.5 text-sm">
          <span className="font-medium text-cyan-900 dark:text-cyan-100 mr-2">{checked.length} selected</span>
          <button type="button" onClick={() => setConfirm({ type: "approve", users: checkedUsers })} className="ar-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
            <FaCheck size={11} /> Accept all
          </button>
          <button type="button" onClick={() => setConfirm({ type: "reject", users: checkedUsers })} className="ar-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium">
            <FaBan size={11} /> Reject all
          </button>
          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Queue + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
        {/* Queue */}
        <div className={`${CARD} ar-in lg:col-span-2 overflow-hidden`} style={{ animationDelay: "300ms" }}>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <input type="checkbox" checked={allChecked} onChange={() => setChecked(allChecked ? [] : list.map((u) => u.user_id))} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select all" />
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {q ? (
                <>
                  <b>{list.length}</b> result{list.length === 1 ? "" : "s"} for "<b>{q}</b>"
                </>
              ) : (
                <>
                  <b>{list.length}</b> in queue
                </>
              )}
            </p>
            <span className="ml-auto hidden lg:inline text-xs text-slate-400">↑ ↓ to move</span>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {loading && !requests.length ? (
              <div className="p-3 space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                ))}
              </div>
            ) : list.length === 0 ? (
              <div className="px-5 py-14 text-center">
                <FaInbox className="mx-auto text-slate-300" size={34} />
                <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">{q ? `No request found for "${q}"` : "No pending admin requests"}</p>
                <p className="text-sm text-slate-500">{q ? "Try a different search." : "New requests from the app will show up here."}</p>
              </div>
            ) : (
              <ul className="p-2 space-y-1.5">
                {list.map((u, i) => {
                  const active = u.user_id === selectedId;
                  const req = parseAny(u.request_date_time);
                  const old = req && Date.now() - req.getTime() > 3 * DAY;
                  const score = checklist(u).filter((c) => c.ok).length;
                  return (
                    <li key={u.user_id} className="ar-in" style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedId(u.user_id);
                          setMobileOpen(true);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && (setSelectedId(u.user_id), setMobileOpen(true))}
                        className={`ar-item flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${
                          active ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20" : "border-transparent hover:bg-slate-50 dark:hover:bg-slate-700/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked.includes(u.user_id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => setChecked((c) => (c.includes(u.user_id) ? c.filter((x) => x !== u.user_id) : [...c, u.user_id]))}
                          className="w-4 h-4 accent-cyan-600 cursor-pointer"
                          aria-label={`Select ${fullName(u)}`}
                        />
                        <Avatar src={u.profile_img} name={fullName(u)} size={44} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">
                            <Hi text={fullName(u)} q={q} />
                          </p>
                          <p className="text-xs text-slate-500 tabular-nums truncate">
                            <Hi text={u.phone_num || u.user_id} q={q} />
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 text-xs ${old ? "text-amber-700 dark:text-amber-300 font-semibold" : "text-slate-500"}`}>
                              {old && <span className="ar-dot w-1.5 h-1.5 rounded-full bg-amber-500" />}
                              {waitingText(req) || "-"}
                            </span>
                            <span className="text-xs text-slate-400">|</span>
                            <span className={`text-xs font-medium ${score >= 5 ? "text-emerald-600" : score >= 3 ? "text-amber-600" : "text-rose-600"}`}>{score}/6 checks</span>
                          </div>
                        </div>
                        <FaChevronRight size={10} className={active ? "text-cyan-600" : "text-slate-300"} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Detail (desktop) */}
        <div className={`${CARD} ar-in hidden lg:flex lg:col-span-3 lg:max-h-[calc(70vh+49px)] overflow-hidden`} style={{ animationDelay: "360ms" }}>
          <div className="w-full">
            {Detail({ u: selected })}
          </div>
        </div>
      </div>

      {/* Detail (mobile sheet) */}
      {mobileOpen && selected && (
        <div className="lg:hidden fixed inset-0 flex items-end" style={{ zIndex: 1100 }}>
          <div className="ar-fade absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <div className="ar-up relative w-full h-[92vh] rounded-t-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl overflow-hidden">
            {Detail({ u: selected })}
          </div>
        </div>
      )}

      {/* Document viewer */}
      {cur && (
        <div className="fixed inset-0 flex flex-col" style={{ zIndex: 1300 }}>
          <div className="ar-fade absolute inset-0 bg-black/85" onClick={() => setViewer(null)} />
          <div className="relative flex items-center justify-between gap-3 px-4 py-3 text-white">
            <p className="font-medium">
              {cur.label} <span className="text-white/60 text-sm">({viewer.index + 1} of {viewer.list.length})</span>
            </p>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="ar-press w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Zoom out"><FaSearchMinus /></button>
              <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="ar-press w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Zoom in"><FaSearchPlus /></button>
              <button type="button" onClick={() => setRotate((r) => (r + 90) % 360)} className="ar-press w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Rotate"><FaRedo /></button>
              <a href={cur.url} target="_blank" rel="noreferrer" className="ar-press w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Open original"><FaExternalLinkAlt size={12} /></a>
              <button type="button" onClick={() => setViewer(null)} className="ar-press w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close"><FaTimes /></button>
            </div>
          </div>
          <div className="relative flex-1 overflow-auto flex items-center justify-center p-4">
            <img
              src={cur.url}
              alt={cur.label}
              onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
              className="ar-img ar-zoom max-w-full max-h-full object-contain cursor-zoom-in select-none"
              style={{ transform: `scale(${zoom}) rotate(${rotate}deg)` }}
            />
            {viewer.list.length > 1 && (
              <>
                <button type="button" onClick={() => setViewer((v) => ({ ...v, index: (v.index - 1 + v.list.length) % v.list.length }))} className="ar-press absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-slate-800 flex items-center justify-center" aria-label="Previous">
                  <FaChevronLeft />
                </button>
                <button type="button" onClick={() => setViewer((v) => ({ ...v, index: (v.index + 1) % v.list.length }))} className="ar-press absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-slate-800 flex items-center justify-center" aria-label="Next">
                  <FaChevronRight />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm */}
      {confirm && confirm.users.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="ar-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="ar-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-11 h-11 rounded-full flex items-center justify-center ${confirm.type === "approve" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {confirm.type === "approve" ? <FaUserShield size={18} /> : <FaExclamationTriangle size={18} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "approve"
                ? confirm.users.length === 1
                  ? `Make ${fullName(confirm.users[0])} an admin?`
                  : `Make ${confirm.users.length} users admins?`
                : confirm.users.length === 1
                ? `Reject ${fullName(confirm.users[0])}'s request?`
                : `Reject ${confirm.users.length} requests?`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {confirm.type === "approve"
                ? "They get admin manager rights right away and move to the Admin List."
                : "The request is removed. Their user account stays as it is and they can apply again."}
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-lg bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.users.map((u) => (
                <div key={u.user_id} className="flex items-center gap-3 p-2.5">
                  <Avatar src={u.profile_img} name={fullName(u)} size={30} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{fullName(u)}</p>
                    <p className="text-xs text-slate-500 tabular-nums">{u.user_id}</p>
                  </div>
                  <span className="text-xs text-slate-500">{checklist(u).filter((c) => c.ok).length}/6</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="ar-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working}
                className={`ar-press flex-1 h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${confirm.type === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : confirm.type === "approve" ? <FaCheck size={12} /> : <FaBan size={12} />}
                {working ? "Please wait..." : confirm.type === "approve" ? "Yes, make admin" : "Yes, reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReq;